import { useQuery } from '@tanstack/react-query'
import {
  createContext,
  use,
  useCallback,
  useState,
  type JSX,
  type ReactNode
} from 'react'

import {
  ALERT_REPORTS_QUERY_KEY,
  fetchAlertReports
} from '../lib/alert-rules-api.ts'
import type { SavedView } from '../lib/saved-views-api.ts'
import { AlertSheet } from './alert-sheet.tsx'

const SavedViewAlertContext = createContext<((view: SavedView) => void) | null>(
  null
)

/**
 * Ask the root layout to open an alert from a saved view.
 *
 * Null when the host is absent. The console always mounts the host beside
 * Alert me; a screen rendered on its own, in a test, gets null and does nothing.
 *
 * @returns The opener, or null.
 */
export function useOpenSavedViewAlert(): ((view: SavedView) => void) | null {
  return use(SavedViewAlertContext)
}

/**
 * SavedViewAlertHost — the alert sheet a saved view starts.
 *
 * The alerts page asks for the sheet. It lives here, next to Alert me, so a
 * page never renders one of its own. The catalogue is the same list Alert me
 * reads, and a report that has left it opens nothing.
 *
 * @param props - Host props.
 * @param props.children - The page, and Alert me.
 * @returns The page, and the sheet when a saved view asked for it.
 */
export function SavedViewAlertHost({
  children
}: {
  children: ReactNode
}): JSX.Element {
  const [saved, setSaved] = useState<SavedView | null>(null)
  const catalogue = useQuery({
    queryKey: ALERT_REPORTS_QUERY_KEY,
    queryFn: fetchAlertReports,
    staleTime: Infinity,
    retry: false
  })
  const report =
    catalogue.data?.reports.find(item => item.id === saved?.report) ?? null
  const openFromSavedView = useCallback((view: SavedView) => {
    setSaved(view)
  }, [])

  return (
    <SavedViewAlertContext value={openFromSavedView}>
      {children}
      {saved && report ? (
        <AlertSheet
          report={report}
          savedView={saved}
          open
          onOpenChange={next => {
            if (!next) {
              setSaved(null)
            }
          }}
        />
      ) : null}
    </SavedViewAlertContext>
  )
}
